import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { scoutAgentService } from "../../services/scoutAgentService";
import { useAgencyLabels } from "../../hooks/scout-request/useAgencyLabels";
import AgentRequestCard from "../../components/scout-request/AgentRequestCard";
import RecommendModal from "../../components/scout-request/RecommendModal";

// Single scout-request detail (route /scout-requests/:id) — the destination of the
// club's "Copy public link" (M2). Agent-gated; shows the one request as the same
// open card and opens the recommend flow. Priority/label marks are pulled per
// request so the agent's own organisation shows here too.
const ScoutRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { labels, createLabel } = useAgencyLabels();
  const [request, setRequest] = useState(null);
  const [mark, setMark] = useState({ priority: null, labels: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recommending, setRecommending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [reqRes, markRes] = await Promise.all([
      scoutAgentService.getRequest(id),
      scoutAgentService.getMarks(),
    ]);
    if (reqRes.success) {
      setRequest(reqRes.data);
    } else {
      setError(reqRes.error || "This scout request could not be found.");
    }
    if (markRes.success) {
      const m = markRes.data.find((x) => x.request === Number(id));
      if (m) setMark({ priority: m.priority ?? null, labels: m.labels || [] });
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const applyMark = (_reqId, m) => setMark({ priority: m.priority ?? null, labels: m.labels || [] });

  return (
    <div className="min-h-screen bg-[#EDF0F4]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate("/scout-requests")}
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#6B7280] hover:text-[#111827] mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Scout requests
        </button>

        {loading ? (
          <div className="bg-white rounded-xl p-5 border border-[#E5E7EB] animate-pulse h-64" />
        ) : error ? (
          <div className="flex flex-col items-center py-20 text-center">
            <p className="text-[#6B7280] mb-3">{error}</p>
            <button onClick={() => navigate("/scout-requests")} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Back to scout requests
            </button>
          </div>
        ) : (
          <AgentRequestCard
            request={{ ...request, mark }}
            labels={labels}
            onRecommend={() => setRecommending(true)}
            onMarkChange={applyMark}
            onCreateLabel={createLabel}
          />
        )}
      </div>

      <RecommendModal
        open={recommending}
        request={request}
        onClose={() => setRecommending(false)}
        onViewMyRecs={() => navigate("/scout-requests/my-recommendations")}
      />
    </div>
  );
};

export default ScoutRequestDetail;
